/**
 * Notification Preferences Service
 *
 * API client for reading and updating user notification channel preferences.
 * Communicates with the Preferences API (GET/PUT /api/notifications/preferences)
 * using the centralized AuthenticatedApiClient for Cognito JWT auth.
 *
 * Requirements: 6.1, 7.1
 */

import { AuthenticatedApiClient } from './authenticated-api-client';

/**
 * Represents the full notification preferences record returned by the API.
 */
export interface NotificationPreferences {
    in_app_enabled: boolean;
    websocket_enabled: boolean;
    email_enabled: boolean;
    email_address: string | null;
}

/**
 * Partial update payload for notification preferences.
 * All fields are optional — only provided fields are updated.
 */
export interface NotificationPreferencesUpdate {
    in_app_enabled?: boolean;
    websocket_enabled?: boolean;
    email_enabled?: boolean;
    email_address?: string;
}

/**
 * Structured error thrown by the preferences service on API failures.
 */
export class NotificationPreferencesError extends Error {
    public readonly statusCode: number;
    public readonly details: string;

    constructor(statusCode: number, message: string, details: string = '') {
        super(message);
        this.name = 'NotificationPreferencesError';
        this.statusCode = statusCode;
        this.details = details;
    }
}

/**
 * Notification Preferences Service class for managing user notification preferences.
 */
export class NotificationPreferencesService {
    private client: AuthenticatedApiClient;

    constructor() {
        this.client = new AuthenticatedApiClient();
    }

    /**
     * Fetches the current user's notification preferences.
     *
     * @returns Promise resolving to the user's notification preferences
     * @throws NotificationPreferencesError on API failure (401, 500, etc.)
     */
    async getPreferences(): Promise<NotificationPreferences> {
        try {
            const response = await this.client.apiRequest('/notifications/preferences', {
                method: 'GET',
            });
            const data = await response.json();
            return data as NotificationPreferences;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Updates the current user's notification preferences.
     *
     * @param prefs - Partial preferences update payload
     * @returns Promise resolving to the full updated preferences record
     * @throws NotificationPreferencesError on validation (400), auth (401), or server (500) errors
     */
    async updatePreferences(prefs: NotificationPreferencesUpdate): Promise<NotificationPreferences> {
        try {
            const response = await this.client.apiRequest('/notifications/preferences', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(prefs),
            });
            const data = await response.json();
            return data as NotificationPreferences;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Parses API errors into typed NotificationPreferencesError instances.
     */
    private handleError(error: unknown): NotificationPreferencesError {
        if (error instanceof NotificationPreferencesError) {
            return error;
        }

        if (error instanceof Error) {
            // AuthenticatedApiClient throws errors in the format:
            // "API request failed: {status} {statusText}\n{body}"
            const match = error.message.match(/API request failed: (\d+)/);
            const statusCode = match ? parseInt(match[1], 10) : 500;

            // Extract the response body (after the newline) for details
            const parts = error.message.split('\n');
            const details = parts.length > 1 ? parts.slice(1).join('\n').trim() : '';

            let message: string;
            switch (statusCode) {
                case 400:
                    message = 'Invalid preferences data';
                    break;
                case 401:
                    message = 'Authentication required. Please sign in again.';
                    break;
                default:
                    message = 'Failed to update notification preferences';
                    break;
            }

            return new NotificationPreferencesError(statusCode, message, details);
        }

        return new NotificationPreferencesError(500, 'An unexpected error occurred');
    }
}
